import React, { useState, useEffect } from 'react';
import { Card, Form, Input, Button, message, Avatar, Row, Col, Typography, Divider, Spin, Descriptions } from 'antd';
import { UserOutlined, MailOutlined, PhoneOutlined, CalendarOutlined, EnvironmentOutlined } from '@ant-design/icons';
import PageHeading from '../../components/PageHeading';
import axiosInstance from '../../utils/axios';

const { Title, Text } = Typography;

const EventProfile = () => {
  const [eventData, setEventData] = useState(null);
  const [staffData, setStaffData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    setLoading(true);
    try {
      // Staff-scoped profile endpoint
      const response = await axiosInstance.get('news_and_events/profile/');
      
      const eventsArray = response.data.events || response.data;
      const event = eventsArray[0];
      
      // Use event staff info from response or derive
      const staff = {
        id: event.id,
        name: event.name || 'Event Staff',
        email: event.email || '',
        mobile: event.mobile || '',
      };

      setEventData(event);
      setStaffData(staff);

      form.setFieldsValue({
        name: staff.name,
        email: staff.email,
        mobile: staff.mobile,
      });
    } catch (error) {
      message.error('Unable to load your profile. Please try again.');
      console.error('Event profile fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async (values) => {
    setSaving(true);
    try {
      const payload = {
        name: values.name,
        email: values.email,
        mobile: values.mobile || null,
      };

      if (values.password) {
        payload.password = values.password;
      }

      await axiosInstance.patch(`news/staff/${staffData.id}/`, payload);
      
      message.success('Profile updated successfully!');
      fetchProfile();
    } catch (error) {
      const errMsg = error.response?.data?.message || error.response?.data?.detail || 'Update failed';
      message.error(errMsg);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '100px 0' }}>
        <Spin size="large" />
        <div style={{ marginTop: 16 }}>Loading your profile...</div>
      </div>
    );
  }

  if (!eventData || !staffData) {
    return (
      <div style={{ textAlign: 'center', padding: '100px 0' }}>
        <Text type="danger">Profile information not found</Text>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <PageHeading name="My Profile" icon={<UserOutlined />} />

      <Row justify="center" style={{ marginTop: 32 }}>
        <Col xs={24} sm={22} md={18} lg={16} xl={14}>
          <Card>
            <div style={{ textAlign: 'center', marginBottom: 32 }}>
              <Avatar size={100} icon={<UserOutlined />} style={{ backgroundColor: '#722ed1' }} />
              <Title level={3} style={{ margin: '16px 0 8px' }}>
                {staffData.name}
              </Title>
              <Text type="secondary">Event Staff Member</Text>
            </div>

            <Divider>My Event</Divider>

            <Descriptions bordered column={1} style={{ marginBottom: 32 }}>
              <Descriptions.Item label={<><CalendarOutlined /> Event Name</>}>
                <strong>{eventData.heading}</strong>
              </Descriptions.Item>
              <Descriptions.Item label="Dates">
                {eventData.start_date} to {eventData.end_date}
              </Descriptions.Item>
              <Descriptions.Item label={<><EnvironmentOutlined /> Location</>}>
                {eventData.location || 'TBA'}
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                {eventData.publish ? 'Published' : 'Draft'}
              </Descriptions.Item>
            </Descriptions>

            <Divider>My Details</Divider>

            <Form form={form} layout="vertical" onFinish={handleUpdate}>
              <Form.Item
                name="name"
                label="Full Name"
                rules={[{ required: true, message: 'Please enter your name' }]}
              >
                <Input prefix={<UserOutlined />} placeholder="Your full name" disabled />
              </Form.Item>

              <Form.Item
                name="email"
                label="Email"
                rules={[
                  { required: true, message: 'Please enter your email' },
                  { type: 'email', message: 'Invalid email format' },
                ]}
              >
                <Input prefix={<MailOutlined />} placeholder="Your email" disabled />
              </Form.Item>

              <Form.Item
                name="mobile"
                label="Mobile Number"
                rules={[
                  { required: true, message: 'Please enter mobile number' },
                  { pattern: /^[0-9]{10}$/, message: 'Enter valid 10-digit number' },
                ]}
              >
                <Input prefix={<PhoneOutlined />} placeholder="9876543210" maxLength={10} disabled />
              </Form.Item>
            </Form>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default EventProfile;

